import React, { useCallback, useRef, useState } from "react";
import { Badge, Button, Form, Table } from "react-bootstrap";
import { Upload, X, AlertTriangle } from "react-feather";

export interface DomainGeoItem {
  domain: string;
  countryCode: string;
}

interface DomainGeoPickerProps {
  selected: DomainGeoItem[];
  onChange: (items: DomainGeoItem[]) => void;
}

function parseCsv(text: string): { items: DomainGeoItem[]; errors: string[] } {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const items: DomainGeoItem[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Skip header row
    if (i === 0 && /^domain/i.test(line) && /country/i.test(line)) {
      continue;
    }

    const parts = line.split(/[,;\t]+/).map((p) => p.trim());
    if (parts.length < 2) {
      errors.push(
        `Line ${i + 1}: expected domain,country_code — got "${line}"`
      );
      continue;
    }
    const domain = parts[0]
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    const countryCode = parts[1].toUpperCase();

    if (!domain || domain.length < 3) {
      errors.push(`Line ${i + 1}: invalid domain "${parts[0]}"`);
      continue;
    }
    if (!/^[A-Z]{2}$/.test(countryCode)) {
      errors.push(
        `Line ${i + 1}: invalid country code "${parts[1]}" (expected 2-letter code)`
      );
      continue;
    }

    const key = `${domain}|${countryCode}`;
    if (!seen.has(key)) {
      seen.add(key);
      items.push({ domain, countryCode });
    }
  }

  return { items, errors };
}

export const DomainGeoPicker: React.FC<DomainGeoPickerProps> = ({
  selected,
  onChange,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [manualDomain, setManualDomain] = useState("");
  const [manualCountry, setManualCountry] = useState("");

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const { items, errors } = parseCsv(text);
        setParseErrors(errors);
        if (items.length > 0) {
          // Merge with existing, dedup
          const existing = new Set(
            selected.map((s) => `${s.domain}|${s.countryCode}`)
          );
          const merged = [
            ...selected,
            ...items.filter(
              (i) => !existing.has(`${i.domain}|${i.countryCode}`)
            ),
          ];
          onChange(merged);
        }
      };
      reader.readAsText(file);
      // Reset so the same file can be re-uploaded
      e.target.value = "";
    },
    [selected, onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const text = e.clipboardData.getData("text");
      if (!text) return;
      e.preventDefault();
      const { items, errors } = parseCsv(text);
      setParseErrors(errors);
      if (items.length > 0) {
        const existing = new Set(
          selected.map((s) => `${s.domain}|${s.countryCode}`)
        );
        const merged = [
          ...selected,
          ...items.filter((i) => !existing.has(`${i.domain}|${i.countryCode}`)),
        ];
        onChange(merged);
      }
    },
    [selected, onChange]
  );

  const addManual = () => {
    const domain = manualDomain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    const cc = manualCountry.trim().toUpperCase();
    if (!domain || !/^[A-Z]{2}$/.test(cc)) return;
    if (selected.some((s) => s.domain === domain && s.countryCode === cc))
      return;
    onChange([...selected, { domain, countryCode: cc }]);
    setManualDomain("");
    setManualCountry("");
  };

  const removeItem = (index: number) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  return (
    <div>
      {/* CSV upload */}
      <div className="d-flex align-items-center gap-2 mb-2">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={14} className="me-1" />
          Upload CSV
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt"
          style={{ display: "none" }}
          onChange={handleFile}
        />
        <span className="text-muted small">
          CSV format: domain, country_code (one per line)
        </span>
      </div>

      {/* Paste area */}
      <Form.Control
        as="textarea"
        rows={3}
        placeholder={
          "Paste CSV data here (domain, country_code):\namazon.co.uk, GB\namazon.de, DE"
        }
        className="mb-2"
        style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
        onPaste={handlePaste}
      />

      {/* Parse errors */}
      {parseErrors.length > 0 && (
        <div
          className="border border-warning rounded p-2 mb-2 small"
          style={{ maxHeight: 100, overflow: "auto" }}
        >
          <AlertTriangle size={14} className="text-warning me-1" />
          <strong>Parse warnings:</strong>
          {parseErrors.map((err, i) => (
            <div key={i} className="text-muted">
              {err}
            </div>
          ))}
        </div>
      )}

      {/* Manual add */}
      <div className="d-flex gap-2 mb-2">
        <Form.Control
          size="sm"
          placeholder="Domain (e.g. amazon.co.uk)"
          value={manualDomain}
          onChange={(e) => setManualDomain(e.target.value)}
          style={{ maxWidth: 250 }}
          onKeyDown={(e) => e.key === "Enter" && addManual()}
        />
        <Form.Control
          size="sm"
          placeholder="Country (e.g. GB)"
          value={manualCountry}
          onChange={(e) => setManualCountry(e.target.value.toUpperCase())}
          style={{ maxWidth: 100 }}
          onKeyDown={(e) => e.key === "Enter" && addManual()}
        />
        <Button size="sm" variant="outline-primary" onClick={addManual}>
          Add
        </Button>
      </div>

      {/* Selected items */}
      {selected.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="fw-bold">{selected.length} items</small>
            <Button
              size="sm"
              variant="link"
              className="text-danger p-0"
              onClick={() => onChange([])}
            >
              Clear All
            </Button>
          </div>
          <div style={{ maxHeight: 250, overflow: "auto" }}>
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Country</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {selected.map((item, i) => (
                  <tr key={`${item.domain}-${item.countryCode}-${i}`}>
                    <td className="small">{item.domain}</td>
                    <td>
                      <Badge bg="secondary">{item.countryCode}</Badge>
                    </td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={() => removeItem(i)}
                      >
                        <X size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};
